/**
 * Fender by SparesHub — offline runtime translator.
 *
 * The entire UI is translated from a single static JSON bundle shipped with
 * the app (`translations.json`, ~380 KB, gzipped ~130 KB). There are NO
 * network calls, NO LLM traffic, NO IndexedDB round-trips and therefore NO
 * language-switch delay. Every candidate DOM text node is looked up in a
 * plain in-memory Map; unknown strings simply stay in English.
 *
 * Multi-toggle safe: the original English source of every text node is
 * remembered in a WeakMap so subsequent switches always translate from
 * English rather than from the currently-displayed script.
 */
import { useEffect, useRef } from "react";
import { useI18n } from "./i18n";
import BUNDLE from "./translations.json";

// ---------- Skip rules ----------
const NUMERIC_RE = /^[\s\d.,:\-/₹Rs$€£¥₽%\u00A0]+$/;
const DOC_NO_RE = /^[A-Z]{2,6}-\d[\d\-]*$/;
const REG_NO_RE = /^[A-Z]{2}[\s\-]?\d{1,2}[\s\-]?[A-Z]{1,3}[\s\-]?\d{1,4}$/;

const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "KBD", "SAMP",
  "TEXTAREA", "INPUT", "SELECT", "OPTION",
]);

function isSkipContainer(el) {
  let cur = el;
  while (cur && cur.nodeType === 1) {
    if (SKIP_TAGS.has(cur.tagName)) return true;
    if (cur.getAttribute && cur.getAttribute("translate") === "no") return true;
    if (cur.classList && cur.classList.contains("notranslate")) return true;
    if (cur.dataset && cur.dataset.notranslate !== undefined) return true;
    cur = cur.parentElement;
  }
  return false;
}

function isTranslatableSource(text) {
  if (!text) return false;
  const t = text.trim();
  if (t.length < 2) return false;
  if (NUMERIC_RE.test(t)) return false;
  if (DOC_NO_RE.test(t)) return false;
  if (REG_NO_RE.test(t)) return false;
  if (!/[A-Za-z]/.test(t)) return false;
  return true;
}

// Preserve leading/trailing whitespace of the original text node
function splitWs(orig) {
  const m = orig.match(/^(\s*)([\s\S]*?)(\s*)$/);
  return { lead: m[1], mid: m[2], tail: m[3] };
}

// ---------- Bundle lookup ----------
// Build a Map<lang, Map<src, translated>> once at module load for the
// fastest possible per-node lookup.
const TABLES = {};
for (const lang of Object.keys(BUNDLE)) {
  const t = new Map();
  for (const [src, tr] of Object.entries(BUNDLE[lang])) t.set(src, tr);
  TABLES[lang] = t;
}
function lookup(lang, src) {
  if (!lang || lang === "en") return undefined;
  const table = TABLES[lang];
  if (!table) return undefined;
  return table.get(src);
}

// ---------- Per-node English source memory ----------
const originalMap = new WeakMap();

// ---------- Core translator ----------
function translateNodes(root, targetLang) {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
      if (isSkipContainer(node.parentElement)) return NodeFilter.FILTER_REJECT;
      if (originalMap.has(node)) return NodeFilter.FILTER_ACCEPT;
      const { mid } = splitWs(node.nodeValue);
      if (!isTranslatableSource(mid)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let cur;
  while ((cur = walker.nextNode())) {
    // Capture original English source once, then always translate from it.
    if (!originalMap.has(cur)) {
      const { mid } = splitWs(cur.nodeValue);
      originalMap.set(cur, mid);
    }
    const src = originalMap.get(cur);
    const { lead, tail } = splitWs(cur.nodeValue);
    const tr = targetLang === "en" ? src : (lookup(targetLang, src) || src);
    const desired = `${lead}${tr}${tail}`;
    if (cur.nodeValue !== desired) cur.nodeValue = desired;
  }
}

// ---------- React wrapper ----------
export function AutoTranslator({ children }) {
  const { lang } = useI18n();
  const rootRef = useRef(null);
  const pendingRef = useRef(null);
  const targetRef = useRef(lang);

  useEffect(() => {
    targetRef.current = lang;
    const mount = () => document.getElementById("app-root") || document.body;

    // Initial sweep — schedule after paint so we translate the current view.
    const t = setTimeout(() => translateNodes(mount(), lang), 20);

    // Observe further DOM changes and translate incrementally — since the
    // bundle lookup is synchronous, this stays near-free even on data-heavy
    // pages like the parts inventory or invoice line-items table.
    const observer = new MutationObserver((mutations) => {
      if (pendingRef.current) return;
      pendingRef.current = requestAnimationFrame(() => {
        pendingRef.current = null;
        const currentLang = targetRef.current;
        for (const m of mutations) {
          if (m.type === "childList") {
            for (const node of m.addedNodes) {
              if (node.nodeType === 1) translateNodes(node, currentLang);
              else if (node.nodeType === 3 && node.parentElement) translateNodes(node.parentElement, currentLang);
            }
          } else if (m.type === "characterData" && m.target?.parentElement) {
            if (!isSkipContainer(m.target.parentElement)) {
              translateNodes(m.target.parentElement, currentLang);
            }
          }
        }
      });
    });

    observer.observe(mount(), { childList: true, subtree: true, characterData: true });

    return () => {
      clearTimeout(t);
      observer.disconnect();
      if (pendingRef.current) cancelAnimationFrame(pendingRef.current);
    };
  }, [lang]);

  return (
    <div id="app-root" ref={rootRef} data-i18n-root>
      {children}
    </div>
  );
}
